import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import { Card } from 'react-bootstrap';
import { FlexRow, TableInMem, exportXlsModal } from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import gql from 'graphql-tag';
import { formatFloat } from 'shared-pages/utils/helpers';
import { Filters } from './filters';

const Materials: Record<number, string> = {
  4: 'Li - Ion',
  3: 'Ni - MH',
  1: 'Pb',
};

export const gqlGetCollectedWeightByMaterialByCollector = gql`
  query getCollectedWeightByMaterialByCollector(
    $startDate: DateTime!
    $endDate: DateTime!
    $materialId: Int!
    $routeTypeId: Int!
    $collectorId: Int
  ) {
    getCollectedWeightByMaterialByCollector(
      startDate: $startDate
      endDate: $endDate
      materialId: $materialId
      routeTypeId: $routeTypeId
      collectorId: $collectorId
    ) {
      data {
        totalWeight
        noRecords
        collectorId
        collectorName
      }
      recordCount
    }
  }
`;

export const CollectedWeightByMaterialByCollectorPage = () => {
  const client = useApolloClient();

  const [queryParams, setQueryParams] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59),
    materialId: undefined,
    routeTypeId: undefined,
  });

  const [totalWeight, setTotalWeight] = useState(0);
  const [noRecords, setNoRecords] = useState(0);

  const [getData, { loading, data: dataRaw }] = useLazyQuery(
    gqlGetCollectedWeightByMaterialByCollector,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        const totalWeight = data?.getCollectedWeightByMaterialByCollector?.data
          ?.map((item) => item.totalWeight || 0)
          .reduce((acc, weight) => acc + weight, 0);
        setTotalWeight(totalWeight);
        const noRecords = data?.getCollectedWeightByMaterialByCollector?.data
          ?.map((item) => item.noRecords || 0)
          .reduce((acc, value) => acc + value, 0);
        setNoRecords(noRecords);
      },
    },
  );

  const data = dataRaw?.getCollectedWeightByMaterialByCollector?.data;
  const recordCount = dataRaw?.getCollectedWeightByMaterialByCollector?.recordCount;

  const filtersChanged = useCallback(
    (values) => {
      setQueryParams(values);
      getData({ variables: { ...values } });
    },
    [getData],
  );

  function useExcelGetQuery() {
    return useLazyQuery(gqlGetCollectedWeightByMaterialByCollector);
  }

  const handleExport = useCallback(async () => {
    const startDate = queryParams.startDate ? new Date(queryParams.startDate) : undefined;
    await exportXlsModal({
      apolloClient: client,
      getLazyQuery: useExcelGetQuery,
      filterVariables: queryParams,
      exportFileName: `Συλλεχθείσα Ποσότητα ${startDate.getFullYear()} - ${Materials[queryParams.materialId]
        }`,
      columnsDef: [
        { fieldName: 'collectorName', fieldTitle: 'Συλλέκτης', fieldType: 'TEXT' },
        { fieldName: 'totalWeight', fieldTitle: 'Συνολικό Βάρος (kg)', fieldType: 'FLOAT' },
        { fieldName: 'noRecords', fieldTitle: 'Πλήθος Εντύπων', fieldType: 'FLOAT' },
      ],
    });
  }, [queryParams]);

  const columns = useMemo(
    () => [
      {
        accessor: 'collectorName',
        Header: 'Συλλέκτης',
      },
      {
        accessor: 'totalWeight',
        Header: 'Συνολικό Βάρος (kg)',
        Cell: ({ row }) => (
          <div className="text-center">{formatFloat(row.original.totalWeight, 2)}</div>
        ),
        Footer: () => <div className="text-center">{formatFloat(totalWeight, 2)}</div>,
      },
      {
        accessor: 'noRecords',
        Header: 'Πλήθος Εντύπων',
        Cell: ({ row }) => (
          <div className="text-center">{formatFloat(row.original.noRecords, 2)}</div>
        ),
        Footer: () => <div className="text-center">{formatFloat(noRecords, 2)}</div>,
      },
    ],
    [totalWeight, noRecords],
  );

  return (
    <Card className="shadow">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0">Συλλεχθείσα Ποσότητα ανά Κατηγορία ανά Συλλέκτη</Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding">
        <Filters onChange={filtersChanged} initialValues={queryParams} />
        <LoadingOverlay active={loading} spinner text="Παρακαλώ περιμένετε...">
          {data ? (
            <TableInMem
              onExport={handleExport}
              columns={columns}
              data={data}
              hasSearch={false}
              hasFooter={true}
              propPageSize={50}
            />
          ) : (
            <div className="empty-table-container"></div>
          )}
        </LoadingOverlay>
      </Card.Body>
    </Card>
  );
};
